import React, { useContext, useMemo } from "react";
import { FabekeDropdown } from "./FabekeDropdown";
import { useTextDomainContext } from "@/app/providers";
import { string, func, bool, oneOf } from "prop-types";
import { AutocompleteDropdown } from "./AutocompleteDropdown";
import { SimpleDropdown } from "./SimpleDropdown";
import { useSubscriptionsList } from "@/hooks";
import type { DropdownOption } from "@/types";

type SubscriptionDropdownProps = {
  componentLabel?: string;
  defaultValue?: string;
  onChange?: (event: DropdownOption) => void;
  required?: boolean;
  visualStyle: "autoComplete" | "autoCompleteMulti" | "simple" | "old";
  fullWidth?: boolean;
};

/**
 * Customer dropdown component
 * @param {object} props props
 * @returns {Element} React Component
 * @class
 */
export const SubscriptionDropdownCommon = (
  props: SubscriptionDropdownProps,
) => {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);
  const { subscriptionsList: options } = useSubscriptionsList({});

  const dropdownOptions = useMemo<DropdownOption[]>(() => {
    return options.map((subscription) => ({
      label: subscription.name || gettext("Subscription Plan"),
      value: subscription.Id || subscription.name || "",
    }));
  }, [gettext, options]);

  //#region useMemo
  const dropdownProps = useMemo(() => {
    const ddProps = {
      options: dropdownOptions,
      componentLabel: gettext("Subscription Plan"),
      defaultValue: "",
      visualStyle: "autoComplete",
      size: "small" as const,
      required: props.required,
      fullWidth: props.fullWidth,
    };

    if (props.componentLabel) {
      ddProps.componentLabel = props.componentLabel;
    }

    if (props.defaultValue) {
      ddProps.defaultValue = props.defaultValue;
    }

    return ddProps;
  }, [
    gettext,
    dropdownOptions,
    props.componentLabel,
    props.defaultValue,
    props.required,
    props.fullWidth,
  ]);
  //#endregion useMemo

  //#region Handlers
  /**
   * Handles change event of the partner dropdown
   * @param {object} event event object
   * @returns {void}
   */
  const handleChange = (event: DropdownOption) => {
    if (typeof props.onChange === "function") {
      props.onChange(event);
    }
  };
  //#endregion Handlers

  return (
    <>
      {props.visualStyle === "autoComplete" && (
        <AutocompleteDropdown onChange={handleChange} {...dropdownProps} />
      )}

      {props.visualStyle === "simple" && (
        <SimpleDropdown onChange={handleChange} {...dropdownProps} />
      )}

      {props.visualStyle === "old" && (
        <FabekeDropdown onChange={handleChange} {...dropdownProps} />
      )}
    </>
  );
};

SubscriptionDropdownCommon.propTypes = {
  componentLabel: string,
  defaultValue: string,
  onChange: func,
  required: bool,
  visualStyle: oneOf(["autoComplete", "autoCompleteMulti", "simple", "old"])
    .isRequired,
};
